import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Grid, Typography } from "@mui/material";
import DataTable from "../../Tables/DataTable/DataTable";
import { AppPieChart } from "../../../common/Chart";
import {
  NO_RECORDS_MESSAGE,
  isAdReportRole,
  isDistributorReportRole,
} from "./adPerformanceReportData";
import "./AdPerformanceReport.css";

const numberFormatter = new Intl.NumberFormat("en-US");

// Map the table column `fieldName` to the column name the API expects in the
// `sortColumn` field of the ad-performance request payload.
const SORT_COLUMN_MAP = {
  bannerName: "bannerName",
  distributorName: "distributorName",
  payment: "paymentStatus",
  adStatus: "adStatus",
};

const KPI_CARDS = [
  { key: "totalClicks", label: "Total Clicks", percentKey: null },
  {
    key: "mobileClicks",
    label: "Mobile Clicks",
    percentKey: "mobileClicksPercentage",
  },
  {
    key: "tabletClicks",
    label: "Tablet Clicks",
    percentKey: "tabletClicksPercentage",
  },
  {
    key: "windowsClicks",
    label: "Windows Clicks",
    percentKey: "windowsClicksPercentage",
  },
];

const formatPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return ` (${n.toFixed(2)}%)`;
};

const AdPerformanceReport = ({
  columns = [],
  rows = [],
  hasSearched = false,
  summary = null,
  pagination = null,
  sortColumn = "",
  sortType = "",
  onPageChange,
  onPageSizeChange,
  onSortChange,
}) => {
  const roleId = Number(localStorage.getItem("selectedRole") || 1);

  const impressionsShareByDevice = useMemo(() => {
    if (!summary) return [];
    return [
      {
        name: "Mobile",
        value: Number(summary.mobileClicks) || 0,
        color: "#1B9CFC",
      },
      {
        name: "Tablet",
        value: Number(summary.tabletClicks) || 0,
        color: "#20C997",
      },
      {
        name: "Windows",
        value: Number(summary.windowsClicks) || 0,
        color: "#FF8C42",
      },
    ];
  }, [summary]);

  // DataTable reads `props.query.SortType` / `SortColumn` directly when
  // rendering its sort buttons, so it must always receive a query object.
  const query = useMemo(
    () => ({
      SortColumn: sortColumn || "",
      SortType: sortType || "",
      PageNumber: pagination?.currentPage || 1,
      PageSize: pagination?.pageSize || 10,
      SearchText: "",
    }),
    [sortColumn, sortType, pagination?.currentPage, pagination?.pageSize],
  );

  // DataTable calls `props.onSort(props.query.SortType, sortField)` — i.e. it
  // hands us the CURRENT sort type so we can flip it to the NEXT one.
  const sortHandler = (currentSortType, sortField) => {
    if (typeof onSortChange !== "function") return;
    const apiSortColumn = SORT_COLUMN_MAP[sortField] || sortField;
    const nextSortType = currentSortType === "asc" ? "desc" : "asc";
    onSortChange(apiSortColumn, nextSortType);
  };

  const handleChangePage = (event, newPage) => {
    if (typeof onPageChange === "function") onPageChange(newPage);
  };

  const handlePageSizeChange = (nextSize) => {
    if (typeof onPageSizeChange === "function") onPageSizeChange(nextSize);
  };

  if (!isAdReportRole(roleId)) {
    return (
      <Box className="ad-performance-report-container" px={4} py={2}>
        <Typography className="ad-performance-report-empty">
          You do not have access to this report.
        </Typography>
      </Box>
    );
  }

  // Distributors only see the dashboard widget when they have at least one ad
  // returned for the current filters. No empty placeholder is rendered otherwise.
  const isDistributor = isDistributorReportRole(roleId);
  const shouldShowWidget = !isDistributor || rows.length > 0;

  return (
    <Box className="ad-performance-report-container" px={4} py={2}>
      <Grid container spacing={2}>
        {shouldShowWidget && summary && rows.length > 0 && (
          <>
            <Grid item xs={12}>
              <Grid container spacing={2} className="ad-performance-kpi-row">
                {KPI_CARDS.map((card) => {
                  const value = Number(summary[card.key]) || 0;
                  const percentText = card.percentKey
                    ? formatPercent(summary[card.percentKey])
                    : "";
                  return (
                    <Grid item xs={12} sm={6} md={3} key={card.key}>
                      <Box className="ad-performance-kpi-card">
                        <Typography className="ad-performance-kpi-label">
                          {card.label}
                        </Typography>
                        <Typography className="ad-performance-kpi-value">
                          {numberFormatter.format(value)}
                          {percentText}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <AppPieChart
                title="Clicks Share by Device"
                subtitle="Distribution of clicks across Mobile, Tablet and Windows"
                data={impressionsShareByDevice}
                variant="donut"
                tooltipFormatter={(value) => numberFormatter.format(value)}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          {rows.length > 0 ? (
            <DataTable
              uniqueKey="adPerformanceId"
              adminTable={rows}
              columns={columns}
              query={query}
              totalPages={pagination?.totalPages || 0}
              pageSize={pagination?.pageSize || 10}
              handleChangePage={handleChangePage}
              onPageSizeChange={handlePageSizeChange}
              onSort={sortHandler}
              serachBarNotRequired={true}
              checkboxesNotRequired={true}
            />
          ) : (
            hasSearched && (
              <Typography className="ad-performance-report-empty">
                {NO_RECORDS_MESSAGE}
              </Typography>
            )
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

AdPerformanceReport.propTypes = {
  columns: PropTypes.array,
  rows: PropTypes.array,
  hasSearched: PropTypes.bool,
  summary: PropTypes.object,
  pagination: PropTypes.shape({
    totalRecords: PropTypes.number,
    totalPages: PropTypes.number,
    currentPage: PropTypes.number,
    pageSize: PropTypes.number,
  }),
  sortColumn: PropTypes.string,
  sortType: PropTypes.string,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  onSortChange: PropTypes.func,
};

export default AdPerformanceReport;
